import { useState } from 'react'
import type { LeaveTypeId } from '@lms/shared-types'
import { hrCancelLeaveRequestSchema } from '@lms/shared-types'
import { Banner } from '../primitives/Banner'
import { Button } from '../primitives/Button'
import { CalcPanel } from '../primitives/CalcPanel'
import { HeadsUp } from '../primitives/HeadsUp'
import { LEAVE_TYPE_LABEL } from '../../lib/leave-type-meta'
import { fmtDateRange } from '../../lib/format'

/**
 * Reusable HR "cancel approved leave" confirmation dialog.
 *
 * Extracted so both the leaves list (Applications tab) and any other HR
 * surface can void an approved leave with the same guarded, audit-logged
 * flow. Takes primitive props (not a full leave object) so any row shape
 * — HrLeaveRow, LeaveListItem — can drive it.
 *
 * The caller owns the mutation: `onConfirm(reason)` should call
 * `cancelHrLeave(id, reason)` and invalidate its own list query. This
 * component only validates the reason (min 10 chars) and renders state.
 */
export interface HrCancelLeaveDialogProps {
  employeeName: string
  leaveType: LeaveTypeId | string
  startDate: string
  endDate: string
  /** Async error surfaced from the caller's mutation (e.g. 409/500). */
  error: string | null
  onClose: () => void
  onConfirm: (reason: string) => Promise<void>
}

export function HrCancelLeaveDialog({
  employeeName,
  leaveType,
  startDate,
  endDate,
  error,
  onClose,
  onConfirm,
}: HrCancelLeaveDialogProps): JSX.Element {
  const [reason, setReason] = useState('')
  const [isPending, setIsPending] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const typeLabel = LEAVE_TYPE_LABEL[leaveType as LeaveTypeId] ?? String(leaveType)

  async function handleSubmit(): Promise<void> {
    const result = hrCancelLeaveRequestSchema.safeParse({ reason })
    if (!result.success) {
      setValidationError('Reason must be at least 10 characters.')
      return
    }
    setValidationError(null)
    setIsPending(true)
    try {
      await onConfirm(reason)
    } finally {
      setIsPending(false)
    }
  }

  const displayedError = validationError ?? error

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cancel approved leave"
      data-testid="hr-cancel-dialog"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        background: 'rgba(0,0,0,0.4)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 14,
          border: '1px solid var(--border)',
          padding: 24,
          maxWidth: 480,
          width: '100%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 17,
            fontWeight: 500,
            letterSpacing: '-0.01em',
            color: 'var(--text)',
            marginBottom: 12,
          }}
        >
          Cancel approved leave
        </h2>

        <div style={{ marginBottom: 12 }}>
          <Banner
            kind="conflict"
            title="This action cannot be undone"
            subtitle="HR cancellation refunds the days, audits the cancel, and notifies the employee and manager."
          />
        </div>

        <CalcPanel
          title="Impact"
          sub="if this leave is cancelled"
          meta="deterministic"
          style={{ marginBottom: 12 }}
        >
          <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6 }}>
            Voiding <strong style={{ color: 'var(--text)' }}>{typeLabel}</strong> for{' '}
            <strong style={{ color: 'var(--text)' }}>{employeeName}</strong> (
            {fmtDateRange(startDate, endDate)}). Taken days will be refunded to their balance.
          </div>
        </CalcPanel>

        <HeadsUp style={{ marginBottom: 12 }}>
          This action is permanent and will be audit-logged. The employee's balance will be
          refunded automatically.
        </HeadsUp>

        <label
          htmlFor="hr-cancel-reason"
          style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}
        >
          Reason <span style={{ color: 'var(--danger-fg)' }}>*</span>
        </label>
        <textarea
          id="hr-cancel-reason"
          data-testid="cancel-reason-input"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value)
            setValidationError(null)
          }}
          placeholder="Provide a reason (min 10 characters)…"
          rows={4}
          disabled={isPending}
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: 8,
            border: `1px solid ${displayedError ? 'var(--danger-line)' : 'var(--border)'}`,
            background: 'var(--surface)',
            fontSize: 14,
            color: 'var(--text)',
            resize: 'vertical',
            boxSizing: 'border-box',
            marginBottom: 8,
          }}
        />

        {displayedError ? (
          <div
            role="alert"
            data-testid="dialog-error"
            style={{
              marginBottom: 12,
              padding: '8px 12px',
              background: 'var(--danger-bg)',
              color: 'var(--danger-fg)',
              border: '1px solid var(--danger-line)',
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            {displayedError}
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <Button
            type="button"
            variant="secondary"
            size="md"
            data-testid="dialog-close"
            onClick={onClose}
            disabled={isPending}
          >
            Keep leave
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="md"
            data-testid="dialog-confirm"
            onClick={() => void handleSubmit()}
            loading={isPending}
          >
            Confirm cancellation
          </Button>
        </div>
      </div>
    </div>
  )
}
